import { GetScenarioGroupQuery, ScenarioGroupDetailsFragment, UpdateScenarioGroupDocument } from '@/generated/graphql';
import { useMutation } from '@vue/apollo-composable';
import { computed, ref, Ref, toValue, watch } from 'vue';
import { z } from 'zod';
import { getScenarioGroupSchema } from '../forms/schemas';
import { ScenarioGroupErrors, ScenarioGroupFields } from '../types';

function setEmptyErrors() {
    return {
        nameShort: [],
        nameLong: [],
        intention: [],
        description: [],
        kapmanNumber: [],
        companyGroup: [],
        businessDomain: [],
        department: [],
        responsible: [],
        organisationalChanges: [],
    };
}

function setEmptyFields() {
    return {
        nameShort: '',
        nameLong: '',
        intention: '',
        description: '',
        kapmanNumber: '',
        companyGroup: '',
        businessDomain: '',
        department: '',
        responsible: '',
    };
}

export function useScenarioGroupForm(scenarioGroup: Ref<ScenarioGroupDetailsFragment>, resetActiveCard: () => void) {
    const schema = computed(() => getScenarioGroupSchema(toValue(scenarioGroup).id));
    type ScenarioGroupSchemaType = z.infer<typeof schema.value>;

    const fields: Ref<ScenarioGroupFields> = ref(setEmptyFields());
    const errors: Ref<ScenarioGroupErrors> = ref(setEmptyErrors());
    const formTouched: Ref<boolean> = ref(false);

    watch(scenarioGroup, (data) => {
        fields.value = {
            nameShort: data.nameShort,
            nameLong: data.nameLong,
            intention: data.intention,
            description: data.description,
            kapmanNumber: data.kapmanNumber,
            companyGroup: data.companyGroup,
            businessDomain: data.businessDomain,
            department: data.department,
            responsible: data.responsible,
        };
    });

    const updateScenarioGroup = useMutation(UpdateScenarioGroupDocument);

    function isFieldTouched(key: string) {
        if (scenarioGroup) {
            const actualValue = scenarioGroup[key as keyof GetScenarioGroupQuery['scenarioGroup']];
            const newValue = fields.value[key];
            return actualValue !== newValue;
        }
    }

    function isFormTouched() {
        const fieldKeys = schema.value.keyof().options;
        if (fieldKeys.some((f) => isFieldTouched(f))) {
            formTouched.value = true;
        } else {
            formTouched.value = false;
        }
    }

    async function onInput(fieldData: { event: Event; fieldName: string }) {
        const { event, fieldName } = fieldData;
        const target = event.target as HTMLInputElement;
        const value = target.value;
        const key = fieldName as keyof ScenarioGroupFields;
        const errorKey = fieldName as keyof ScenarioGroupErrors;
        const schemaKey = fieldName as keyof ScenarioGroupSchemaType;
        const parse = await schema.value.shape[schemaKey].safeParseAsync(value);
        fields.value[key] = value;
        if (parse.success) {
            errors.value[errorKey] = [];
        } else {
            errors.value[errorKey] = parse.error.format()._errors;
        }
        isFormTouched();
    }

    function updateFields(data: ScenarioGroupFields) {
        fields.value = { ...data };
    }

    function discardChanges() {
        const data = scenarioGroup.value;
        const formData = {
            nameShort: data.nameShort,
            nameLong: data.nameLong,
            intention: data.intention,
            description: data.description,
            kapmanNumber: data.kapmanNumber,
            companyGroup: data.companyGroup,
            businessDomain: data.businessDomain,
            department: data.department,
            responsible: data.responsible,
        };
        updateFields(formData);
        resetActiveCard();
    }

    async function onUpdateScenarioGroup() {
        const fieldValues = fields.value;
        const parse = await schema.value.safeParseAsync(fieldValues);
        if (parse.success) {
            const params = { id: toValue(scenarioGroup).id, ...fieldValues };
            await updateScenarioGroup.mutate(params);
            return true;
        }
        return false;
    }

    return {
        fields,
        formTouched,
        errors,
        onInput,
        onUpdateScenarioGroup,
        discardChanges,
    };
}
